import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import api from '@/lib/axios';
import { toast } from 'sonner';
import Layout from '@/components/layout/Layout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { formatScheduleLabel, formatTime, tariffFormName } from '@/lib/utils';

const DAYS = [
  { value: '0', label: 'Montag' },
  { value: '1', label: 'Dienstag' },
  { value: '2', label: 'Mittwoch' },
  { value: '3', label: 'Donnerstag' },
  { value: '4', label: 'Freitag' },
  { value: '5', label: 'Samstag' },
  { value: '6', label: 'Sonntag' },
];

const EMPTY_FORM = {
  name: '',
  subject_id: '',
  teacher_id: '',
  room_id: '',
  tariff_id: '',
  course_type: 'group',
  duration_minutes: '45',
  rhythm: 'weekly',
  max_capacity: '1',
  day_of_week: '',
  start_time: '',
  status: 'active',
  description: '',
};

function toTimeInput(value) {
  if (!value) return '';
  const s = String(value);
  return s.length >= 5 ? s.slice(0, 5) : s;
}

function asList(data) {
  if (Array.isArray(data)) return data;
  if (data?.items && Array.isArray(data.items)) return data.items;
  return [];
}

export default function CourseEdit() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [subjects, setSubjects] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [tariffs, setTariffs] = useState([]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [courseRes, subjectsRes, teachersRes, roomsRes, tariffsRes] = await Promise.all([
          api.get(`/courses/${id}`),
          api.get('/subjects').catch(() => ({ data: [] })),
          api.get('/teachers', { params: { limit: 500 } }).catch(() => ({ data: [] })),
          api.get('/locations/rooms').catch(() => api.get('/rooms').catch(() => ({ data: [] }))),
          api.get('/tariffs-discounts/tariffs').catch(() => ({ data: [] })),
        ]);
        if (cancelled) return;
        const c = courseRes.data;
        setForm({
          name: c.name || '',
          subject_id: c.subject_id || '',
          teacher_id: c.teacher_id || '',
          room_id: c.room_id || '',
          tariff_id: c.tariff_id || '',
          course_type: c.course_type || 'group',
          duration_minutes: String(c.duration_minutes ?? 45),
          rhythm: c.rhythm || 'weekly',
          max_capacity: String(c.max_capacity ?? 1),
          day_of_week: c.day_of_week != null ? String(c.day_of_week) : '',
          start_time: toTimeInput(c.start_time),
          status: c.status || 'active',
          description: c.description || '',
        });
        setSubjects(asList(subjectsRes.data));
        setTeachers(asList(teachersRes.data));
        setRooms(asList(roomsRes.data));
        setTariffs(asList(tariffsRes.data));
      } catch {
        toast.error('Fehler beim Laden des Kurses');
        navigate(`/courses/${id}`);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [id, navigate]);

  const set = (key, value) => setForm((f) => ({ ...f, [key]: value }));

  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) {
      toast.error('Bitte einen Kursnamen angeben');
      return;
    }
    if (!form.subject_id) {
      toast.error('Bitte ein Fach wählen');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        subject_id: form.subject_id,
        teacher_id: form.teacher_id || null,
        room_id: form.room_id || null,
        tariff_id: form.tariff_id || null,
        course_type: form.course_type,
        duration_minutes: parseInt(form.duration_minutes, 10) || 45,
        rhythm: form.rhythm,
        max_capacity: parseInt(form.max_capacity, 10) || 1,
        day_of_week: form.day_of_week !== '' ? parseInt(form.day_of_week, 10) : null,
        start_time: form.start_time || null,
        status: form.status,
        description: form.description.trim() || null,
      };
      await api.put(`/courses/${id}`, payload);
      toast.success('Kurs gespeichert');
      navigate(`/courses/${id}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Speichern');
    } finally {
      setSaving(false);
    }
  };

  const fieldCls = 'h-9 text-sm';
  const selectCls = 'h-9 w-full rounded-md border border-slate-300 bg-white px-2 text-sm';

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-screen">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-red" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="p-6 md:p-8 max-w-3xl" data-testid="course-edit-page">
        <button
          type="button"
          onClick={() => navigate(`/courses/${id}`)}
          className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-800 mb-4"
          data-testid="course-edit-back"
        >
          <ArrowLeft className="w-4 h-4" />
          Zurück zum Kurs
        </button>

        <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6">
          <h1 className="text-2xl font-bold text-slate-900 mb-1">Kurs bearbeiten</h1>
          <p className="text-sm text-slate-500 mb-6">
            {formatScheduleLabel(form.name) || form.name || '—'}
          </p>

          <form onSubmit={handleSave} className="space-y-5">
            <div>
              <Label htmlFor="course-name">Kursname</Label>
              <Input
                id="course-name"
                className={fieldCls}
                value={form.name}
                onChange={(e) => set('name', e.target.value)}
                data-testid="course-edit-name"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="course-subject">Fach</Label>
                <select
                  id="course-subject"
                  className={selectCls}
                  value={form.subject_id}
                  onChange={(e) => set('subject_id', e.target.value)}
                  data-testid="course-edit-subject"
                >
                  <option value="">Fach wählen…</option>
                  {subjects.map((s) => (
                    <option key={s.id} value={s.id}>{s.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <Label htmlFor="course-teacher">Lehrkraft</Label>
                <select
                  id="course-teacher"
                  className={selectCls}
                  value={form.teacher_id}
                  onChange={(e) => set('teacher_id', e.target.value)}
                  data-testid="course-edit-teacher"
                >
                  <option value="">— ohne Lehrkraft —</option>
                  {teachers.map((t) => (
                    <option key={t.id} value={t.id}>
                      {t.last_name}, {t.first_name}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <Label htmlFor="course-tariff">Tarif</Label>
                <select
                  id="course-tariff"
                  className={selectCls}
                  value={form.tariff_id}
                  onChange={(e) => set('tariff_id', e.target.value)}
                  data-testid="course-edit-tariff"
                >
                  <option value="">— keiner —</option>
                  {tariffs.map((t) => (
                    <option key={t.id} value={t.id}>{tariffFormName(t.name)}</option>
                  ))}
                </select>
              </div>
              <div>
                <Label htmlFor="course-room">Raum</Label>
                <select
                  id="course-room"
                  className={selectCls}
                  value={form.room_id}
                  onChange={(e) => set('room_id', e.target.value)}
                  data-testid="course-edit-room"
                >
                  <option value="">— kein Raum —</option>
                  {rooms.map((r) => (
                    <option key={r.id} value={r.id}>
                      {r.name}{r.location_name ? ` (${r.location_name})` : ''}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div>
                <Label htmlFor="course-day">Wochentag</Label>
                <select
                  id="course-day"
                  className={selectCls}
                  value={form.day_of_week}
                  onChange={(e) => set('day_of_week', e.target.value)}
                  data-testid="course-edit-day"
                >
                  <option value="">—</option>
                  {DAYS.map((d) => (
                    <option key={d.value} value={d.value}>{d.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <Label htmlFor="course-time">Uhrzeit</Label>
                <Input
                  id="course-time"
                  type="time"
                  className={fieldCls}
                  value={formatTime(form.start_time) || ''}
                  onChange={(e) => set('start_time', e.target.value)}
                  data-testid="course-edit-time"
                />
              </div>
              <div>
                <Label htmlFor="course-duration">Dauer (Min.)</Label>
                <Input
                  id="course-duration"
                  type="number"
                  min="5"
                  className={fieldCls}
                  value={form.duration_minutes}
                  onChange={(e) => set('duration_minutes', e.target.value)}
                  data-testid="course-edit-duration"
                />
              </div>
              <div>
                <Label htmlFor="course-capacity">Kapazität</Label>
                <Input
                  id="course-capacity"
                  type="number"
                  min="1"
                  className={fieldCls}
                  value={form.max_capacity}
                  onChange={(e) => set('max_capacity', e.target.value)}
                  data-testid="course-edit-capacity"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <Label htmlFor="course-type">Typ</Label>
                <select
                  id="course-type"
                  className={selectCls}
                  value={form.course_type}
                  onChange={(e) => set('course_type', e.target.value)}
                  data-testid="course-edit-type"
                >
                  <option value="individual">Einzelunterricht</option>
                  <option value="group">Gruppenunterricht</option>
                  <option value="workshop">Workshop</option>
                </select>
              </div>
              <div>
                <Label htmlFor="course-rhythm">Rhythmus</Label>
                <select
                  id="course-rhythm"
                  className={selectCls}
                  value={form.rhythm}
                  onChange={(e) => set('rhythm', e.target.value)}
                  data-testid="course-edit-rhythm"
                >
                  <option value="weekly">Wöchentlich</option>
                  <option value="biweekly">14-tägig</option>
                  <option value="individual">individuell</option>
                </select>
              </div>
              <div>
                <Label htmlFor="course-status">Status</Label>
                <select
                  id="course-status"
                  className={selectCls}
                  value={form.status}
                  onChange={(e) => set('status', e.target.value)}
                  data-testid="course-edit-status"
                >
                  <option value="active">Aktiv</option>
                  <option value="inactive">Inaktiv</option>
                  <option value="paused">Pausiert</option>
                </select>
              </div>
            </div>

            <div>
              <Label htmlFor="course-description">Beschreibung</Label>
              <textarea
                id="course-description"
                rows={3}
                className="w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm"
                value={form.description}
                onChange={(e) => set('description', e.target.value)}
                data-testid="course-edit-description"
              />
            </div>

            <div className="flex items-center justify-end gap-3 pt-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate(`/courses/${id}`)}
                data-testid="course-edit-cancel"
              >
                Abbrechen
              </Button>
              <Button
                type="submit"
                disabled={saving}
                className="bg-slate-900 text-white hover:bg-slate-800"
                data-testid="course-edit-save"
              >
                {saving ? 'Speichern…' : 'Speichern'}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </Layout>
  );
}
